import { useState, useRef, useEffect } from 'react';
import { aiAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Sparkles,
  Send,
  Loader2,
  User,
  Bot,
  Headphones
} from 'lucide-react';

export default function AIChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await aiAPI.chatbot(input, sessionId);
      setSessionId(res.data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: res.data.response }
      ]);
    } catch (err) {
      console.error('AI chat error:', err);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-white border-slate-200" data-testid="ai-chat">
      <CardContent className="p-0">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-lg shadow-cyan-500/20">
            <Headphones className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">AI Support Assistant</h3>
            <p className="text-sm text-slate-500">Powered by GPT-5.2</p>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="h-[400px] p-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center mx-auto mb-4">
                <Bot className="w-8 h-8 text-cyan-600" />
              </div>
              <h3 className="font-medium mb-2 text-slate-800">How can I help you today?</h3>
              <p className="text-sm text-slate-500 max-w-sm mx-auto">
                Ask me anything about our products, services, or troubleshooting steps.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {['How do I reset my password?', 'What are your pricing plans?', 'I need help with billing'].map((q) => (
                  <button
                    key={q}
                    onClick={() => setInput(q)}
                    className="px-3 py-1.5 rounded-full bg-slate-100 text-sm text-slate-600 hover:bg-cyan-50 hover:text-cyan-700 transition-colors border border-slate-200"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, i) => (
                <div
                  key={`${msg.role}-${i}-${msg.content?.substring(0, 20)}`}
                  className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  data-testid={`chat-message-${i}`}
                >
                  <Avatar className="w-8 h-8 flex-shrink-0 border border-slate-200">
                    {msg.role === 'user' ? (
                      <AvatarFallback className="bg-slate-100 text-slate-600"><User className="w-4 h-4" /></AvatarFallback>
                    ) : (
                      <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-teal-500 text-white">
                        <Sparkles className="w-4 h-4" />
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className={`max-w-[80%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                    <div className={`p-3 rounded-xl ${msg.role === 'user'
                      ? 'bg-cyan-500 text-white'
                      : 'bg-slate-100 text-slate-700'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-3">
                  <Avatar className="w-8 h-8 border border-slate-200">
                    <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-teal-500 text-white">
                      <Sparkles className="w-4 h-4" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="p-3 rounded-xl bg-slate-100">
                    <Loader2 className="w-4 h-4 animate-spin text-cyan-500" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <form onSubmit={handleSend} className="p-4 border-t border-slate-200">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question..."
              className="bg-slate-50 border-slate-200"
              disabled={loading}
              data-testid="ai-chat-input"
            />
            <Button
              type="submit"
              className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white"
              disabled={loading || !input.trim()}
              data-testid="ai-chat-send-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
